import { forwardRef, useCallback } from "react";
import { useField } from "formik";
import PropTypes from "prop-types";
import classNames from "classnames";
import HorizontalDotLoader from "app/pages/.shared/HorizontalDotLoader";
import "./UploadFile.scss";

// forwardRef est nécessaire pour le fonctionnement de la lib react-phone-number-input
// @see https://github.com/catamphetamine/react-phone-number-input#inputcomponent
const UploadFile = forwardRef(
	(
		{ id, isRequired, disabled, loading, label, note, "data-testid": dataCy, ...restProps },
		ref
	) => {
		const [field, meta, helpers] = useField(restProps);

		const inputClassName = classNames("upload-file", {
			"upload-file--required": isRequired,
			"upload-file--touched": meta.touched || field.value || loading,
			"upload-file--error": meta.touched && meta.error,
			"upload-file--disabled": disabled,
			"upload-file--loading": loading,
		});

		// put the label on top when input is focused
		const setTouchedOnFocus = useCallback(() => {
			helpers.setTouched(true);
		}, []);

		// put the label back to input middle when input is blur without value
		const setUntouchedOnBlur = useCallback(event => {
			if (event.target.value === "") {
				helpers.setTouched(false);
			}
		}, []);

		return (
			<div className={inputClassName} data-testid={dataCy}>
				<label htmlFor={id} className="upload-file__label">
					{label}
				</label>
				{loading ? (
					<div className="upload-file__loader">
						<HorizontalDotLoader dotSize="3px" color="#3493dc" />
					</div>
				) : (
					<input
						ref={ref}
						type="file"
						id={id}
						disabled={disabled}
						{...field}
						{...restProps}
						onFocus={setTouchedOnFocus}
						onBlur={setUntouchedOnBlur}
						className="upload-file__input"
					/>
				)}
				{note && <div className="upload-file__note"> {note}</div>}
			</div>
		);
	}
);

UploadFile.defaultProps = {
	type: "text",
};

UploadFile.propTypes = {
	id: PropTypes.string.isRequired, // required for label
	name: PropTypes.string.isRequired, // required for Formik.useField()
	type: PropTypes.string,
	isRequired: PropTypes.bool,
	disabled: PropTypes.bool,
	loading: PropTypes.bool,
	label: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
	note: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
	"data-testid": PropTypes.string,
};

export default UploadFile;
